import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as Sentry from '@sentry/react';
import type { PublicUser } from '@evi/shared';

// Sentry user context — ID only, no PII. Pattern from audit HIGH 1.2.
// Centralized in this store so every login + logout path keeps it
// in sync without remembering a separate Sentry.setUser call.
function syncSentryUser(user: PublicUser | null): void {
  if (user) {
    Sentry.setUser({ id: user._id });
  } else {
    Sentry.setUser(null);
  }
}

interface AuthState {
  user: PublicUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  setAuth: (user: PublicUser, accessToken: string) => void;
  clearAuth: () => void;
  setAccessToken: (token: string) => void;
  setUser: (user: PublicUser) => void;
  /** Re-fetch /auth/me and update the store. Used after side-effects
   *  that change server-derived fields the auth store mirrors —
   *  notably `has_payment_method`, which flips when the driver adds
   *  or removes a Stripe payment method. Swallows errors (a transient
   *  /auth/me failure shouldn't log the driver out — the existing
   *  user object stays in place). */
  refreshUser: () => Promise<void>;
  /** Driver Themes ticket — optimistically merge fields into the
   *  persisted PublicUser without firing a /auth/me refetch. Used by
   *  surfaces that just wrote the same fields to the server and want
   *  the auth store to reflect the new values immediately (theme
   *  picker apply flow, etc.). No-op when user is null. Generic
   *  shape so future surfaces can reuse the action for any subset
   *  of PublicUser fields. Mirror of the admin store's updateUser
   *  action shipped in Phase 7g.4b.4 Commit 2; PWA gets the same
   *  pattern here so the theme apply flow can sync active_theme_
   *  family / theme_mode without a refetch round-trip. */
  updateUser: (patch: Partial<PublicUser>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      setAuth: (user, accessToken) => {
        syncSentryUser(user);
        set({ user, accessToken, isAuthenticated: true });
      },
      clearAuth: () => {
        syncSentryUser(null);
        set({ user: null, accessToken: null, isAuthenticated: false });
      },
      setAccessToken: (token) => set({ accessToken: token }),
      setUser: (user) => {
        syncSentryUser(user);
        set({ user });
      },
      updateUser: (patch) =>
        set((state) => {
          if (!state.user) return {};
          const next = { ...state.user, ...patch };
          syncSentryUser(next);
          return { user: next };
        }),
      refreshUser: async () => {
        // Dynamic import to break the circular dep with @/lib/api
        // (api.ts imports useAuthStore for the token; if this
        // module imported fetchMe at top level the cycle would
        // hang during initial module load).
        try {
          const { fetchMe } = await import('@/lib/api');
          const fresh = await fetchMe();
          // Only update if still authenticated — guards against the
          // race where the driver logs out mid-fetch.
          if (useAuthStore.getState().isAuthenticated) {
            syncSentryUser(fresh);
            set({ user: fresh });
          }
        } catch {
          // Soft-fail. Stale auth-store user is better than
          // forcing a logout on a transient /auth/me hiccup.
        }
      },
    }),
    {
      name: 'evi-web-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
